import { Badge } from '@/components/shadcn/badge'
import { getContrastTextColor, hexToHslaOrPassthrough } from '@/lib/utils'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import clsx from 'clsx'
import { format as formatNumber } from 'd3-format'
import { format as formatDate } from 'date-fns'
import { ReactNode, useState } from 'react'
import { ProgressStatus } from '../DataTableComponent/ProgressStatus'
import {
  DataTableV2BadgeColumnDataDisplay,
  DataTableV2BooleanColumnDataDisplay,
  DataTableV2Column,
  DataTableV2DataRowTitleValueObject,
  DataTableV2DateStringColumnDataDisplay,
  DataTableV2NumberColumnDataDisplay,
  DataTableV2ProgressStatus,
  DataTableV2ProgressStatusColumnDataDisplay,
  DataTableV2StatusIconColumnDataDisplay,
  DataTableV2StringColumnDataDisplay,
} from './types'
import { isProgressStatusArray, isProgressStatusObject, isTitleValueObject } from './utils'
import { IconBg } from '@/components/ui/IconBg'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { StatusColor, StatusIndicatorConfig } from '../FormComponent/types'
import { computeStatusColorForValue } from '../FormComponent/statusIndicatorUtils'

interface RenderCellV2Props {
  dataDisplay: DataTableV2Column['dataDisplay']
  value: unknown
}

/**
 * Checks if a value is an array of title-value objects
 */
const isTitleValueObjectArray = (value: unknown): value is DataTableV2DataRowTitleValueObject[] => {
  return Array.isArray(value) && value.length > 0 && value.every(isTitleValueObject)
}

/**
 * Renders a string value with optional truncation
 */
const RenderString = ({
  value,
  shouldTruncate,
  maxWidth,
}: {
  value: string
  shouldTruncate?: boolean
  maxWidth?: number
}) => {
  const [isTruncated, setIsTruncated] = useState(shouldTruncate || false)
  if (!shouldTruncate) {
    // return <span className="whitespace-nowrap">{value}</span>
    return (
      <span className="block" style={{ maxWidth: maxWidth ? `${maxWidth}px` : '200px' }}>
        {value}
      </span>
    )
  }

  return (
    // <span className="block truncate" style={{ maxWidth: maxWidth ? `${maxWidth}px` : '200px' }} title={value}>
    <span
      onClick={() => setIsTruncated((curr) => !curr)}
      role="button"
      className={clsx({ 'line-clamp-1': isTruncated, 'block break-words': !isTruncated })}
      style={{ maxWidth: maxWidth ? `${maxWidth}px` : '200px' }}
      title={value}
    >
      {value}
    </span>
  )
}

/**
 * Renders an array of strings, joined with commas
 */
const RenderStringArray = ({
  values,
  shouldTruncate,
  maxWidth,
}: {
  values: string[]
  shouldTruncate?: boolean
  maxWidth?: number
}) => {
  const joinedValue = values.join(', ')
  return <RenderString value={joinedValue} shouldTruncate={shouldTruncate} maxWidth={maxWidth} />
}

/**
 * Renders a date string value with formatting
 */
const RenderDateString = ({ value, formatter }: { value: string; formatter?: string }) => {
  try {
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      return <span className="whitespace-nowrap">{value}</span>
    }
    const formatted = formatDate(date, formatter || 'PPp')
    return <span className="whitespace-nowrap">{formatted}</span>
  } catch {
    return <span className="whitespace-nowrap">{value}</span>
  }
}

/**
 * Renders an array of date strings, joined with commas
 */
const RenderDateStringArray = ({ values, formatter }: { values: string[]; formatter?: string }) => {
  const formattedDates = values.map((value) => {
    try {
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return value
      }
      return formatDate(date, formatter || 'PPp')
    } catch {
      return value
    }
  })
  return <span className="whitespace-nowrap">{formattedDates.join(', ')}</span>
}

/**
 * Maps StatusColor to Badge variant
 */
const statusColorToBadgeVariant: Record<StatusColor, 'error' | 'warning' | 'success'> = {
  red: 'error',
  amber: 'warning',
  green: 'success',
}

/**
 * Renders a number value with optional formatting
 */
const RenderNumber = ({
  value,
  formatter,
  statusIndicator,
}: {
  value: number
  formatter?: string
  statusIndicator?: StatusIndicatorConfig
}) => {
  let displayValue: string | number = value
  if (formatter) {
    try {
      displayValue = formatNumber(formatter)(value)
    } catch {
      // keep raw value on format failure
    }
  }

  const statusColor = statusIndicator ? computeStatusColorForValue(value, statusIndicator) : undefined

  if (statusColor) {
    return (
      <Badge variant={statusColorToBadgeVariant[statusColor]} className="h-5 whitespace-nowrap text-sm tabular-nums">
        {displayValue}
      </Badge>
    )
  }

  return <span className="whitespace-nowrap tabular-nums">{displayValue}</span>
}

/**
 * Renders an array of numbers, joined with commas
 */
const RenderNumberArray = ({ values, formatter }: { values: number[]; formatter?: string }) => {
  const formattedNumbers = values.map((value) => {
    if (formatter) {
      try {
        return formatNumber(formatter)(value)
      } catch {
        return String(value)
      }
    }
    return String(value)
  })
  return <span className="whitespace-nowrap tabular-nums">{formattedNumbers.join(', ')}</span>
}

/**
 * Renders a boolean value with optional check/cross icons
 */
const RenderBoolean = ({ value, displayChecks }: { value: boolean; displayChecks?: boolean }) => {
  if (displayChecks) {
    return (
      <div className="pl-3">
        {value ? (
          <IconCheckMarkOutline className="text-green-800" size={12} />
        ) : (
          <IconCloseOutline className="text-red-800" size={12} />
        )}
      </div>
    )
  }
  return <span className="whitespace-nowrap">{value ? 'Yes' : 'No'}</span>
}

/**
 * Renders a badge component
 */
const RenderBadge = ({
  value,
  colorMapping,
}: {
  value: string | DataTableV2DataRowTitleValueObject
  colorMapping?: Record<string, string>
}) => {
  const color = colorMapping?.[typeof value === 'string' ? value : value.value]

  return (
    <Badge
      variant="outline"
      className={clsx('whitespace-nowrap', { 'border-none': !!color })}
      // style={color ? { borderColor: color, color: color, backgroundColor: `${color}10` } : undefined}
      style={color ? { backgroundColor: color, color: getContrastTextColor(color) } : undefined}
    >
      {typeof value === 'string' ? value : value.title}
    </Badge>
  )
}

/**
 * Renders an array of badges
 */
const RenderBadgeArray = ({
  values,
  colorMapping,
}: {
  values: string[] | DataTableV2DataRowTitleValueObject[]
  colorMapping?: Record<string, string>
}) => {
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((value, index) => (
        <RenderBadge
          key={`${typeof value === 'string' ? value : value.value}-${index}`}
          value={value}
          colorMapping={colorMapping}
        />
      ))}
    </div>
  )
}

/**
 * Renders a status icon with color and tooltip based on value mappings
 */
const RenderStatusIcon = ({
  value,
  iconMapping,
  colorMapping,
  tooltipMapping,
}: {
  value: string
  iconMapping: Record<string, string>
  colorMapping?: Record<string, string>
  tooltipMapping: Record<string, string>
}) => {
  const color = colorMapping?.[value]
  const bgStyle = color ? { backgroundColor: hexToHslaOrPassthrough(color, 0.1), border: `1px solid ${color}` } : {}
  const iconStyle = color ? { color: color } : {}
  const icon = iconMapping[value]
  const tooltip = tooltipMapping[value]

  return (
    <div className="flex w-full items-center justify-center">
      <TooltipButton side="left" tooltip={tooltip}>
        <IconBg style={bgStyle} className="size-6 shadow">
          <DynamicIcon size={14} iconName={icon} style={iconStyle} />
        </IconBg>
      </TooltipButton>
    </div>
  )
}

/**
 * Renders a title-value object, displaying the title
 */
const RenderTitleValue = ({ value }: { value: DataTableV2DataRowTitleValueObject }) => {
  return <span className="whitespace-nowrap">{value.title}</span>
}

/**
 * Renders an array of title-value objects, displaying all titles joined with commas
 */
const RenderTitleValueArray = ({ values }: { values: DataTableV2DataRowTitleValueObject[] }) => {
  const titles = values.map((item) => item.title).join(', ')
  return <span className="whitespace-nowrap">{titles}</span>
}

/**
 * Main cell renderer for V2 that delegates to specific renderers based on data display configuration
 */
export const RenderCellV2 = ({ dataDisplay, value }: RenderCellV2Props): ReactNode => {
  if (value === null || value === undefined) {
    return null
  }

  // Handle arrays of title-value objects first - display all titles
  if (isTitleValueObjectArray(value)) {
    if ('component' in dataDisplay && dataDisplay.component === 'Badge') {
      const badgeDisplay = dataDisplay as DataTableV2BadgeColumnDataDisplay
      return <RenderBadgeArray values={value} colorMapping={badgeDisplay.colorMapping} />
    }
    return <RenderTitleValueArray values={value} />
  }

  // Handle single title-value objects - display the title
  if (isTitleValueObject(value)) {
    if ('component' in dataDisplay && dataDisplay.component === 'Badge') {
      const badgeDisplay = dataDisplay as DataTableV2BadgeColumnDataDisplay
      return (
        <RenderBadge
          value={value as string | DataTableV2DataRowTitleValueObject}
          colorMapping={badgeDisplay.colorMapping}
        />
      )
    }
    if ('component' in dataDisplay && dataDisplay.component === 'StatusIcon') {
      const statusIconDisplay = dataDisplay as DataTableV2StatusIconColumnDataDisplay
      const lookupValue = String((value as DataTableV2DataRowTitleValueObject).value)
      return (
        <RenderStatusIcon
          value={lookupValue}
          iconMapping={statusIconDisplay.iconMapping}
          colorMapping={statusIconDisplay.colorMapping}
          tooltipMapping={statusIconDisplay.tooltipMapping}
        />
      )
    }
    return <RenderTitleValue value={value as DataTableV2DataRowTitleValueObject} />
  }

  // Handle component-based data display
  if ('component' in dataDisplay) {
    const componentDisplay = dataDisplay as
      | DataTableV2BadgeColumnDataDisplay
      | DataTableV2ProgressStatusColumnDataDisplay
      | DataTableV2StatusIconColumnDataDisplay

    if (componentDisplay.component === 'Badge') {
      const badgeDisplay = dataDisplay as DataTableV2BadgeColumnDataDisplay
      // Handle array of badges
      if (Array.isArray(value)) {
        return <RenderBadgeArray values={value} colorMapping={badgeDisplay.colorMapping} />
      }
      return (
        <RenderBadge
          value={value as string | DataTableV2DataRowTitleValueObject}
          colorMapping={badgeDisplay.colorMapping}
        />
      )
    }

    if (componentDisplay.component === 'ProgressStatus') {
      if (isProgressStatusArray(value)) {
        return <ProgressStatus value={value as DataTableV2ProgressStatus[]} />
      }
      if (isProgressStatusObject(value)) {
        return <ProgressStatus value={[value as DataTableV2ProgressStatus]} />
      }
      return null
    }

    if (componentDisplay.component === 'StatusIcon') {
      const statusIconDisplay = dataDisplay as DataTableV2StatusIconColumnDataDisplay
      return (
        <RenderStatusIcon
          value={value as string}
          iconMapping={statusIconDisplay.iconMapping}
          colorMapping={statusIconDisplay.colorMapping}
          tooltipMapping={statusIconDisplay.tooltipMapping}
        />
      )
    }
  }

  // Handle type-based data display
  if ('type' in dataDisplay) {
    switch (dataDisplay.type) {
      case 'string': {
        const stringDisplay = dataDisplay as DataTableV2StringColumnDataDisplay
        // Handle array of strings
        if (Array.isArray(value)) {
          return (
            <RenderStringArray
              values={value.map(String)}
              shouldTruncate={stringDisplay.truncate}
              maxWidth={stringDisplay.width}
            />
          )
        }
        return (
          <RenderString value={String(value)} shouldTruncate={stringDisplay.truncate} maxWidth={stringDisplay.width} />
        )
      }

      case 'date-string': {
        const dateDisplay = dataDisplay as DataTableV2DateStringColumnDataDisplay
        // Handle array of date strings
        if (Array.isArray(value)) {
          return <RenderDateStringArray values={value.map(String)} formatter={dateDisplay.dateFormatter} />
        }
        return <RenderDateString value={String(value)} formatter={dateDisplay.dateFormatter} />
      }

      case 'number': {
        const numberDisplay = dataDisplay as DataTableV2NumberColumnDataDisplay
        const statusIndicator = numberDisplay.statusIndicator
        // Handle array of numbers
        if (Array.isArray(value)) {
          return <RenderNumberArray values={value.map(Number)} formatter={numberDisplay.numberFormatter} />
        }
        return (
          <RenderNumber
            value={Number(value)}
            formatter={numberDisplay.numberFormatter}
            statusIndicator={statusIndicator}
          />
        )
      }

      case 'boolean': {
        const booleanDisplay = dataDisplay as DataTableV2BooleanColumnDataDisplay
        return <RenderBoolean value={Boolean(value)} displayChecks={booleanDisplay.displayChecks} />
      }
    }
  }

  // Fallback: render as string
  if (typeof value === 'object') {
    return <span className="whitespace-nowrap">{JSON.stringify(value)}</span>
  }

  return <span className="whitespace-nowrap">{String(value)}</span>
}
